<template>
  <div class="saldo-page">
    <!-- Header -->
    <div class="page-header mb-4">
      <div class="d-flex align-center">
        <div class="header-icon-pill mr-3">
          <v-icon color="white" size="22">mdi-wallet</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-0">Mi Saldo</h1>
          <p class="page-subtitle mb-0">Balance actual de tu cuenta</p>
        </div>
        <v-spacer />
        <v-btn icon class="glass-btn" @click="getData()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- KPI cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <div class="kpi-card kpi-blue">
          <div class="kpi-icon-wrap"><v-icon color="white" size="24">mdi-cash-multiple</v-icon></div>
          <div class="kpi-label">Saldo</div>
          <div class="kpi-value">₡{{ fmt(editedItem.saldo) }}</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="kpi-card kpi-amber">
          <div class="kpi-icon-wrap"><v-icon color="white" size="24">mdi-timer-sand</v-icon></div>
          <div class="kpi-label">Saldo Flotante</div>
          <div class="kpi-value">₡{{ fmt(editedItem.flotante) }}</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="kpi-card kpi-green">
          <div class="kpi-icon-wrap"><v-icon color="white" size="24">mdi-shield-check</v-icon></div>
          <div class="kpi-label">Límite Asignado</div>
          <div class="kpi-value">{{ editedItem.limite > 0 ? '₡' + fmt(editedItem.limite) : 'Sin límite' }}</div>
        </div>
      </v-col>
    </v-row>

    <!-- Barra de progreso si tiene límite -->
    <v-card v-if="editedItem.limite > 0" class="glass-card rounded-xl pa-5 mb-4" elevation="0">
      <div class="d-flex justify-space-between align-center mb-2">
        <span class="text-body-2 font-weight-bold">Uso del límite</span>
        <span class="text-body-2 font-weight-bold" :style="{ color: porcentaje >= 90 ? '#ef4444' : porcentaje >= 70 ? '#f59e0b' : '#22c55e' }">
          {{ porcentaje }}%
        </span>
      </div>
      <v-progress-linear
        :value="porcentaje"
        :color="porcentaje >= 90 ? 'error' : porcentaje >= 70 ? 'warning' : 'success'"
        height="10" rounded background-color="rgba(0,0,0,0.08)"
      />
      <div class="d-flex justify-space-between mt-1">
        <span class="text-caption" style="opacity:0.5;">₡0</span>
        <span class="text-caption" style="opacity:0.5;">₡{{ fmt(editedItem.limite) }}</span>
      </div>
      <!-- Total comprometido -->
      <div class="comprometido-row mt-3">
        <div class="d-flex align-center" style="gap:6px;">
          <v-icon size="16" color="#64748b">mdi-wallet-outline</v-icon>
          <span class="comprometido-label">Total comprometido</span>
        </div>
        <div class="d-flex align-center" style="gap:6px;">
          <span class="comprometido-value">₡{{ fmt(totalComprometido) }}</span>
          <span class="comprometido-detail">(₡{{ fmt(editedItem.saldo) }} saldo + ₡{{ fmt(editedItem.flotante) }} flotante)</span>
        </div>
      </div>
    </v-card>

    <Snackbar :text="snackText" :snackbar="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "SaldoBalance",
  components: { Snackbar },
  data() {
    return {
      snackText: "", snackValue: false,
      saldo: 0, flotante: 0, limite: 0,
      loading: false,
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),
    editedItem() {
      return { saldo: this.saldo, flotante: this.flotante, limite: this.limite };
    },
    porcentaje() {
      if (!this.limite) return 0;
      return Math.min(Math.round(((this.saldo + this.flotante) / this.limite) * 100), 100);
    },
    totalComprometido() {
      return this.saldo + this.flotante;
    },
  },
  created() { this.getData(); },
  methods: {
    getData() {
      this.loading = true;
      axios.get(`api/Tiempos/GetSaldoBalance?idUsuario=${this.user.cedula}&companyId=${this.strCia}`)
        .then(r => {
          const d = r.data;
          if (d) {
            this.saldo    = d.saldo    || 0;
            this.flotante = d.flotante || 0;
            this.limite   = d.limite   || 0;
          }
        })
        .catch(e => console.log(e))
        .finally(() => { this.loading = false; });
    },
    fmt(val) { return val ? Number(val).toLocaleString("es-CR") : "0"; },
  },
};
</script>



<style scoped>
.saldo-page { padding: 20px 24px; width: 100%; }
.page-header { animation: fadeIn 0.4s ease; }
.header-icon-pill {
  width: 46px; height: 46px; border-radius: 14px;
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(25,118,210,0.35); flex-shrink: 0;
}
.page-title { font-size: 1.3rem; font-weight: 800; letter-spacing: -0.3px; }
.page-subtitle { font-size: 0.8rem; color: #64748b; }
.theme--dark .page-subtitle { color: #94a3b8; }
.glass-btn { backdrop-filter: blur(8px); }
.glass-card {
  background: rgba(255,255,255,0.75) !important;
  backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255,255,255,0.5) !important;
}
.theme--dark .glass-card { background: rgba(30,30,46,0.75) !important; border-color: rgba(255,255,255,0.08) !important; }

/* KPI cards */
.kpi-card {
  border-radius: 18px; padding: 20px;
  display: flex; flex-direction: column; gap: 8px;
  position: relative; overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.kpi-card:hover { transform: translateY(-2px); }
.kpi-card::before {
  content: ''; position: absolute; top: -20px; right: -20px;
  width: 100px; height: 100px; border-radius: 50%;
  background: rgba(255,255,255,0.1);
}
.kpi-blue  { background: linear-gradient(135deg, #1976D2, #42A5F5); box-shadow: 0 8px 24px rgba(25,118,210,0.35); }
.kpi-amber { background: linear-gradient(135deg, #F59E0B, #FBBF24); box-shadow: 0 8px 24px rgba(245,158,11,0.35); }
.kpi-green { background: linear-gradient(135deg, #059669, #34D399); box-shadow: 0 8px 24px rgba(5,150,105,0.35); }
.kpi-icon-wrap {
  width: 40px; height: 40px; border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
}
.kpi-label { font-size: 0.75rem; font-weight: 600; color: rgba(255,255,255,0.8); text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-value { font-size: 1.6rem; font-weight: 900; color: white; letter-spacing: -0.5px; line-height: 1.1; }
/* Comprometido row */
.comprometido-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border-radius: 12px;
  background: rgba(25,118,210,0.07);
  border: 1px solid rgba(25,118,210,0.12);
  flex-wrap: wrap; gap: 6px;
}
.theme--dark .comprometido-row { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.08); }
.comprometido-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.theme--dark .comprometido-label { color: #94a3b8; }
.comprometido-value { font-size: 1rem; font-weight: 800; color: #1976D2; }
.theme--dark .comprometido-value { color: #60A5FA; }
.comprometido-detail { font-size: 0.72rem; color: #94a3b8; }
@keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
</style>